/**
 * Small formatting helpers used across the dossier views. Kept
 * dependency-free so they tree-shake cleanly when only one is imported.
 */

/** Format an integer with French thousand separators (NBSP-free). */
export function fmt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString('fr-FR').replace(/ /g, ' ');
}

/** Human-readable byte size: 42191714 → "40,2 Mo". */
export function fmtBytes(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n) || n < 0) return '—';
  if (n < 1024) return `${n} o`;
  const units = ['Ko', 'Mo', 'Go', 'To'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const rounded = v >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
  return `${rounded.toLocaleString('fr-FR')} ${units[i]}`;
}

/**
 * Pretty-print an E.164 number the way Ivory Coast operators expect:
 *   +2250707000123 → "+225 07 07 00 01 23"
 * Falls back to the input untouched when the format doesn't match.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const m = phone.match(/^(\+225)(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/);
  if (!m) return phone;
  return `${m[1]} ${m[2]} ${m[3]} ${m[4]} ${m[5]} ${m[6]}`;
}

/**
 * Render an ISO 8601 timestamp as "2026-06-13 18:42" — matches the MAFA
 * design's monospaced tabular style and avoids locale surprises on
 * different operator browsers.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Extract initials for the avatar chip. Two characters max, upper-case.
 */
export function initials(text: string | null | undefined): string {
  if (!text) return '??';
  return text
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('') || '??';
}

export const ATTR_TYPE_LABELS: Record<string, string> = {
  ENROLLMENT: 'Enrôlement',
  ID30_RECORD: 'Fiche recensement PDE.CI',
  CALL: 'Appel',
  PROFILE_UPDATE: 'Mise à jour profil',
  TRANSACTION: 'Transaction',
  TRANSACTION_SUMMARY: 'Résumé transactions',
  INSPECTION_VISIT: 'Visite inspection',
  CARD_ACTIVATION: 'Activation de carte',
  MANUAL_NOTE: 'Note manuelle',
  CONSENT_GRANTED: 'Consentement accordé',
  CONSENT_REVOKED: 'Consentement retiré',
  ACTOR_REGISTERED: "Enrôleur déclaré",
  ACTOR_REVOKED: "Enrôleur révoqué",
  IDENTITY_MERGE: "Conflit arbitré",
  TAX_PAYMENT: "Paiement taxe",
};

export function attrTypeLabel(t: string): string {
  return ATTR_TYPE_LABELS[t] ?? t;
}

export const ATTR_TYPE_ICONS: Record<string, string> = {
  CALL: 'call',
  TRANSACTION: 'dollar',
  TRANSACTION_SUMMARY: 'dollar',
  ID30_RECORD: 'database',
  INSPECTION_VISIT: 'pin',
  MANUAL_NOTE: 'doc',
  PROFILE_UPDATE: 'edit',
  TAX_PAYMENT: 'flag',
  ENROLLMENT: 'user',
  ACTOR_REGISTERED: 'users',
  ACTOR_REVOKED: 'users',
  CONSENT_GRANTED: 'shieldcheck',
  CONSENT_REVOKED: 'alert',
  IDENTITY_MERGE: 'link',
  CARD_ACTIVATION: 'card',
};

export function attrTypeIcon(t: string): string {
  return ATTR_TYPE_ICONS[t] ?? 'dot';
}

export const SOURCE_LABELS: Record<string, string> = {
  ORANGE: 'Orange Money',
  ID30: 'PDE.CI (État)',
  CALL_CENTER: 'Call Center',
  ORANGE_PROFIL: 'Orange — Profil marchands',
  ORANGE_MENSUEL: 'Orange — Activité mensuelle',
  PARTNER_BATCH: 'Lot partenaire',
  PARTNER: 'Lot partenaire',
  CRYSTAL: 'Crystal',
  MAFA_BO: 'MAFA Back-Office',
  MANUAL: 'Saisie manuelle',
};

export function sourceLabel(s: string): string {
  return SOURCE_LABELS[s] ?? s;
}
